const TOKEN_KEY = 'sf-token';

// The JWT lives in localStorage; the API client reads it on every request and
// sends it as `Authorization: Bearer <token>`. (credentials:'include' is kept
// too — harmless for token auth.)
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// The redux store registers a handler here so a 401 can clear global auth state.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

function handleUnauthorized(): void {
  localStorage.removeItem(TOKEN_KEY);
  onUnauthorized?.();
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  const headers = new Headers(extra);
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function readError(res: Response): Promise<string> {
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    if (body.error || body.message) message = body.error ?? body.message ?? message;
  } catch {
    /* ignore */
  }
  return message;
}

export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(authHeaders(init?.headers));
  if (!headers.has('content-type')) headers.set('content-type', 'application/json');
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!res.ok) {
    if (res.status === 401) handleUnauthorized();
    throw new ApiError(await readError(res), res.status);
  }
  return (await res.json()) as T;
}

export async function upload<T>(path: string, fd: FormData): Promise<T> {
  // Do not set content-type for multipart — the browser sets the boundary.
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: fd,
  });
  if (!res.ok) {
    if (res.status === 401) handleUnauthorized();
    throw new ApiError(await readError(res), res.status);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

// Build a download URL for <a href download> links (which cannot send headers):
// append the JWT as a query param so the backend can authenticate the request.
export function downloadUrl(path: string): string {
  const token = getToken();
  if (!token) return `/api${path}`;
  const sep = path.includes('?') ? '&' : '?';
  return `/api${path}${sep}token=${encodeURIComponent(token)}`;
}
